
import { useEffect, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { MyCourseCardSkeleton } from "@/features/my-courses/components/my-course-card-skeleton";
import { UserService } from "@/services/user-service";
import MyCourseCard from "@/features/my-courses/components/my-course-card";
import LoginRequired from "@/components/login-required";
import DotsLoading from "@/components/dots-loading";
import { useProfileStore } from "@/store/profile-store";

const PER_PAGE = 15;

const LIST_GRID = "grid w-full gap-3 p-4 md:grid-cols-2 lg:grid-cols-3";
const FULL_ROW = "md:col-span-2";

export default function MyCoursesList() {
  const accessToken = useProfileStore((s) => s.tokens?.access_token);

  const {
    data,
    isError,
    isPending,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["my-courses"],
    enabled: Boolean(accessToken),
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      UserService.getMyCourses({ page: pageParam as number, per_page: PER_PAGE }),
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.pagination;
      if (!pagination) return undefined;
      return pagination.current_page < pagination.last_page
        ? pagination.current_page + 1
        : undefined;
    },
    // refetch every time to keep the list up-to-date (e.g. after a new purchase)
    staleTime: 0,
    refetchOnMount: "always",
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  const courses = data?.pages.flatMap((pg) => pg.data ?? []) ?? [];

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, courses.length]);

  if (!accessToken) {
    return (
      <LoginRequired
        title="برای مشاهده دوره‌های خود وارد شوید"
        description="پس از ورود، دوره‌های خریداری‌شده و در حال یادگیری شما اینجا نمایش داده می‌شوند."
      />
    );
  }

  // Only block on the first load (no cached data). Background refetches keep the
  // existing cards mounted instead of flashing skeletons over the whole list.
  if (isPending) {
    return (
      <div className={LIST_GRID}>
        {Array.from({ length: 4 }).map((_, i) => (
          <MyCourseCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  return (
    <>
      <div className={LIST_GRID}>
        {isError && (
          <p className={`text-danger ${FULL_ROW}`}>
            مشکلی در دریافت اطلاعات وجود دارد.
          </p>
        )}

        {courses.length === 0 ? (
          <div className={`text-center py-8 text-default-600 ${FULL_ROW}`}>
            شما هیچ دوره‌ای ندارید
          </div>
        ) : (
          courses.map((course) => (
            <MyCourseCard key={course.id} course={course} />
          ))
        )}

        {/* Infinite scroll sentinel + loader */}
        {hasNextPage && (
          <div
            ref={sentinelRef}
            className={`flex justify-center py-4 ${FULL_ROW}`}
          >
            {isFetchingNextPage && <DotsLoading />}
          </div>
        )}
      </div>
    </>
  );
}
